import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, ErrorBox, Skeleton } from "../../components/ui";
import { useNotificationMutation, useNotificationSettings } from "../../lib/api";

const ROUTES = ["in_app", "email", "mobile"] as const;
type Route = (typeof ROUTES)[number];

/** Notification settings (9.2): one switch per feature, per delivery route.
 *
 * The routing model has existed since WP-05 with nothing reading it. This screen is one half of fixing that —
 * the other half is the dispatcher — and it is built so a switch cannot lie: a route that is on but
 * undeliverable shows the reason next to it rather than appearing to work. */
export default function NotificationSettings() {
  const { t } = useTranslation();
  const q = useNotificationSettings();
  const save = useNotificationMutation();
  const [tested, setTested] = useState<Record<string, string>>({});

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-32" />)}</div>;
  const d = q.data;

  const toggle = (code: string, route: Route, on: boolean) =>
    save.mutate({ method: "set_feature", params: { event: code, [route === "in_app" ? "in_app" : route]: on ? 1 : 0 } });

  const test = (code: string) =>
    save.mutate({ method: "send_test", params: { event: code } }, {
      onSuccess: (r) => {
        const sent = Object.keys((r.sent ?? {}) as Record<string, unknown>);
        const blocked = Object.keys((r.blocked ?? {}) as Record<string, unknown>);
        setTested((p) => ({ ...p, [code]: sent.length ? t("admin.notif.testSent", { routes: sent.join(", ") })
          : t("admin.notif.testBlocked", { routes: blocked.join(", ") || "—" }) }));
      },
    });

  return (
    <div className="mt-4 space-y-6" data-admin-notifications>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-3xl text-sm text-brand-grey">{t("admin.notif.hint")}</p>
        <div className="text-right text-xs text-brand-grey">
          {t("admin.notif.summary", { on: d.summary.on, total: d.summary.features })}
          {d.summary.blocked > 0 && <div className="text-status-amber">{t("admin.notif.summaryBlocked", { n: d.summary.blocked })}</div>}
        </div>
      </div>

      {!d.readiness.can_send && (
        <div className="rounded-lg border border-status-amber/40 bg-amber-50 px-4 py-3 text-sm" data-admin-notif-banner>
          <div className="font-semibold text-status-amber">{t("admin.notif.emailUnavailable")}</div>
          <ul className="mt-1 list-disc pl-5 text-[12px] text-brand-charcoal">
            {d.readiness.blockers.map((b) => <li key={b}>{b}</li>)}
          </ul>
          <p className="mt-1 text-[11px] text-brand-grey">{t("admin.notif.emailUnavailableHint")}</p>
        </div>
      )}

      {d.tracks.map((track) => {
        const rows = d.features.filter((f) => f.track === track);
        if (!rows.length) return null;
        return (
          <Card key={track} className="overflow-hidden" data-admin-notif-track={track}>
            <div className="border-b border-brand-grey-light px-4 py-3">
              <h3 className="font-heading text-base font-semibold">{track}</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
                <tr>
                  <th className="px-4 py-2">{t("admin.notif.feature")}</th>
                  <th className="px-4 py-2">{t("admin.notif.audience")}</th>
                  {ROUTES.map((r) => <th key={r} className="px-2 py-2 text-center">{t(`admin.notif.route.${r}`)}</th>)}
                  <th className="px-4 py-2 text-right">{t("admin.notif.test")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.code} className="border-t border-brand-grey-light" data-admin-notif-row={f.code}>
                    <td className="px-4 py-2">
                      <div className="font-medium">{f.label}</div>
                      <div className="text-[11px] text-brand-grey">
                        <code>{f.code}</code>
                        {!f.configured && <span className="ml-2">{t("admin.notif.usingDefaults")}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-[11px] text-brand-grey">{f.audience}</td>
                    {ROUTES.map((route) => {
                      const on = f.routes[route];
                      const blocked = f.blocked[route];
                      return (
                        <td key={route} className="px-2 py-2 text-center">
                          <label className="inline-flex flex-col items-center gap-0.5">
                            <input type="checkbox" checked={on} disabled={save.isPending}
                              data-admin-notif-toggle={`${f.code}:${route}`}
                              onChange={(e) => toggle(f.code, route, e.target.checked)} />
                            {on && blocked && (
                              <span className="text-[10px] font-semibold text-status-amber" title={blocked.join("; ")}>
                                {t("admin.notif.cannotDeliver")}
                              </span>
                            )}
                          </label>
                        </td>
                      );
                    })}
                    <td className="px-4 py-2 text-right">
                      {tested[f.code]
                        ? <span className="text-[11px] text-brand-grey">{tested[f.code]}</span>
                        : <button onClick={() => test(f.code)} disabled={save.isPending} data-admin-notif-test={f.code}
                            className="text-[11px] font-semibold text-brand-primary hover:underline disabled:opacity-50">
                            {t("admin.notif.sendTest")}
                          </button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        );
      })}

      {save.isError && <ErrorBox error={save.error} />}
    </div>
  );
}
